import { equals, isEmpty, trim } from 'ramda';
import { useEffect, useState } from 'react';

import { useAppSelector } from '@/shared/hooks';

import { toothModel } from '@/entities/tooth';

import { useAddNewComment } from './useAddNewComment';

export const useCommentTooth = (comment: string, toothID: string) => {
  const [isCommentEditorVisible, setIsCommentEditorVisible] = useState(false);
  const [isNewCommentLoading, setIsNewCommentLoading] = useState(false);
  const [newComment, setNewComment] = useState('');

  const addNewComment = useAddNewComment();

  const commentForEdit = useAppSelector(
    toothModel.selectors.selectToothCommentForEdit(toothID),
  );

  const isUserSaved = useAppSelector(
    toothModel.selectors.selectIsToothCommentUserSaved(toothID),
  );

  const newCommentWithoutWhitespace = trim(newComment);

  const isNewCommentEmpty = isEmpty(newCommentWithoutWhitespace);

  const isCommentExisting =
    Boolean(comment) || Boolean(newCommentWithoutWhitespace);

  const isNewCommentNotEqualsCurrentComment = isCommentExisting
    ? !equals(newCommentWithoutWhitespace, trim(comment))
    : undefined;

  const resetNewComment = () => setNewComment('');

  useEffect(() => {
    if (!comment) {
      resetNewComment();
    }
  }, [comment]);

  const closeAndResetTextInEditor = () => {
    setIsCommentEditorVisible(false);

    resetNewComment();
  };

  const openChangeComment = () => {
    setIsCommentEditorVisible(true);

    // Note: If no user-saved comment, prefill with AI suggestion (if any)
    setNewComment(isUserSaved ? comment : commentForEdit);
  };

  const changeCommentInEditor = (text: string) => setNewComment(text);

  const saveComment = async (toothID: string) => {
    if (isNewCommentNotEqualsCurrentComment) {
      setIsNewCommentLoading(true);

      try {
        await addNewComment(toothID, newComment);
      } finally {
        setIsNewCommentLoading(false);
      }
    }

    if (isNewCommentEmpty) {
      setIsCommentEditorVisible(false);
    } else {
      setIsCommentEditorVisible(false);
    }
  };

  return {
    newComment,
    isCommentEditorVisible,
    isNewCommentLoading,
    closeAndResetTextInEditor,
    openChangeComment,
    changeCommentInEditor,
    saveComment,
  };
};
